// Version 2: each row is [rowOffset, columnOffset, length, ...] on the 3 m grid.
const CELL_SIZE = 3;
const RADIUS = 6378137;
const EARTH_RADIUS = 6371008.8;
const invalid = () => { throw new Error('INVALID_APPLICATION_FOOTPRINT'); };

const normalize = value => {
    const footprint = typeof value === 'string' ? JSON.parse(value) : value;
    if (!footprint || footprint.version !== 2 || footprint.cellSize !== CELL_SIZE ||
        !Array.isArray(footprint.origin) || footprint.origin.length !== 2 ||
        !footprint.origin.every(Number.isSafeInteger) || !Array.isArray(footprint.rows) || !footprint.rows.length) invalid();
    const [originX, originY] = footprint.origin;
    let previousY = -1;
    for (const row of footprint.rows) {
        if (!Array.isArray(row) || row.length < 3 || row.length % 2 !== 1 ||
            !row.every(Number.isSafeInteger) || row[0] < 0 || row[0] <= previousY ||
            !Number.isSafeInteger(originY + row[0])) invalid();
        previousY = row[0];
        let previousEnd = -1;
        for (let index = 1; index < row.length; index += 2) {
            const start = row[index], length = row[index + 1];
            if (start < 0 || length <= 0 || start <= previousEnd ||
                !Number.isSafeInteger(start + length - 1) ||
                !Number.isSafeInteger(originX + start) ||
                !Number.isSafeInteger(originX + start + length - 1)) invalid();
            previousEnd = start + length - 1;
        }
    }
    return { version: 2, cellSize: CELL_SIZE, origin: footprint.origin, rows: footprint.rows };
};

const fromCells = cells => {
    const rows = new Map();
    let originX = Infinity, originY = Infinity;
    for (const cell of cells) {
        if (typeof cell !== 'string' || !/^-?\d+:-?\d+$/.test(cell)) invalid();
        const [column, row] = cell.split(':').map(Number);
        if (!Number.isSafeInteger(column) || !Number.isSafeInteger(row)) invalid();
        originX = Math.min(originX, column);
        originY = Math.min(originY, row);
        if (!rows.has(row)) rows.set(row, new Set());
        rows.get(row).add(column);
    }
    if (!rows.size) invalid();
    const compactRows = Array.from(rows).sort((a, b) => a[0] - b[0]).map(([row, columns]) => {
        const sorted = Array.from(columns).sort((a, b) => a - b);
        const result = [row - originY];
        let first = sorted[0], last = first;
        for (let index = 1; index < sorted.length; index++) {
            const column = sorted[index];
            if (column === last + 1) { last = column; continue; }
            result.push(first - originX, last - first + 1);
            first = last = column;
        }
        result.push(first - originX, last - first + 1);
        return result;
    });
    return normalize({ version: 2, cellSize: CELL_SIZE, origin: [originX, originY], rows: compactRows });
};

const read = value => {
    if (!value) return null;
    try { return normalize(value); } catch (error) { return null; }
};

const intersect = (left, right) => {
    const result = [];
    let a = 0, b = 0;
    while (a < left.length && b < right.length) {
        const first = Math.max(left[a][0], right[b][0]);
        const last = Math.min(left[a][1], right[b][1]);
        if (first <= last) result.push([first, last]);
        if (left[a][1] < right[b][1]) a++; else b++;
    }
    return result;
};

const pointInRing = (x, y, ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i], [xj, yj] = ring[j];
        if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
};

// Same cell-center inclusion as the former point-by-point comparison.
const ringIntervals = (ring, row) => {
    const y = (row + 0.5) * CELL_SIZE;
    const crossings = [];
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i], [xj, yj] = ring[j];
        if ((yi > y) !== (yj > y)) crossings.push((xj - xi) * (y - yi) / (yj - yi) + xi);
    }
    crossings.sort((a, b) => a - b);
    const result = [];
    for (let index = 0; index + 1 < crossings.length; index += 2) {
        let first = Math.floor(crossings[index] / CELL_SIZE - 0.5);
        let last = Math.ceil(crossings[index + 1] / CELL_SIZE - 0.5);
        // Resolve floating-point boundary rounding with the original predicate.
        while (first <= last && !pointInRing((first + 0.5) * CELL_SIZE, y, ring)) first++;
        while (first <= last && !pointInRing((last + 0.5) * CELL_SIZE, y, ring)) last--;
        if (first <= last) result.push([first, last]);
    }
    return merge(result);
};

const merge = intervals => {
    const result = [];
    intervals.sort((a, b) => a[0] - b[0]);
    for (const interval of intervals) {
        const previous = result[result.length - 1];
        if (previous && interval[0] <= previous[1] + 1) previous[1] = Math.max(previous[1], interval[1]);
        else result.push(interval.slice());
    }
    return result;
};

const count = intervals => intervals.reduce((sum, [first, last]) => sum + last - first + 1, 0);
const absoluteIntervals = (footprint, row) => {
    const result = [];
    for (let index = 1; index < row.length; index += 2) {
        const first = footprint.origin[0] + row[index];
        result.push([first, first + row[index + 1] - 1]);
    }
    return result;
};

const intersectionArea = (left, right, polygonRings) => {
    let a = 0, b = 0, area = 0;
    while (a < left.rows.length && b < right.rows.length) {
        const leftRow = left.rows[a], rightRow = right.rows[b];
        const row = left.origin[1] + leftRow[0];
        const otherRow = right.origin[1] + rightRow[0];
        if (row < otherRow) { a++; continue; }
        if (row > otherRow) { b++; continue; }
        let intervals = intersect(absoluteIntervals(left, leftRow), absoluteIntervals(right, rightRow));
        if (intervals.length && polygonRings) intervals = intersect(intervals, ringIntervals(polygonRings[0], row));
        let cells = count(intervals);
        if (cells && polygonRings && polygonRings.length > 1) {
            const holes = merge(polygonRings.slice(1).flatMap(ring => ringIntervals(ring, row)));
            cells -= count(intersect(intervals, holes));
        }
        if (cells) {
            const lower = row * CELL_SIZE / RADIUS, upper = (row + 1) * CELL_SIZE / RADIUS;
            area += cells * EARTH_RADIUS ** 2 * CELL_SIZE / RADIUS * (Math.tanh(upper) - Math.tanh(lower));
        }
        a++; b++;
    }
    return area;
};

export { normalize, fromCells, read, intersectionArea };

